import { jsx, jsxs } from "react/jsx-runtime";
import { useThemeProps } from "@mui/material";
import { forwardRef } from "react";
import { useSlotProps } from "../../../base/index.js";
import { useToolbar } from "../hooks/useToolbar.js";
import { useToolbarButton } from "../hooks/useToolbarButton.js";
import { toolbarButtonName } from "./classes.js";
import { Badge } from "./components/Badge.js";
import { Icon } from "./components/Icon.js";
import { Label } from "./components/Label.js";
import { Root } from "./components/Root.js";
import { Tooltip } from "./components/Tooltip.js";
import { useUtilityClasses } from "./hooks/useUtilityClasses.js";
const defaultBadgeAnchorOrigin = {
  horizontal: "right",
  vertical: "top"
};
const ToolbarButton = forwardRef(function ToolbarButton2(inProps, ref) {
  const props = useThemeProps({
    name: toolbarButtonName,
    props: inProps
  });
  const {
    className = "",
    classes,
    components,
    componentsProps,
    hoverBulge = true,
    indicator,
    label,
    sx,
    testId = `${toolbarButtonName}-root`,
    tooltip = label,
    tooltipPlacement = "right",
    value,
    ...rest
  } = props;
  const { icon, onClick, selected, showLabel } = useToolbarButton(props);
  const ownerState = { classes, hoverBulge, indicator, selected, showLabel };
  const utilityClasses = useUtilityClasses(ownerState);
  const { cssVariables } = useToolbar();
  const rootSlotProps = useSlotProps({
    className: `${className} ${utilityClasses.root}`,
    elementType: Root,
    externalSlotProps: componentsProps?.root,
    ownerState,
    additionalProps: {
      "data-testid": testId,
      "data-tidal": toolbarButtonName,
      as: components?.Root,
      cssVariables,
      onClick,
      ref,
      sx,
      ...value && { "aria-pressed": selected },
      ...rest
    }
  });
  const tooltipSlotProps = useSlotProps({
    className: utilityClasses.tooltip,
    elementType: Tooltip,
    externalSlotProps: componentsProps?.tooltip,
    ownerState: void 0,
    additionalProps: {
      "data-testid": `${testId}-tooltipRoot`,
      as: components?.Tooltip,
      placement: tooltipPlacement,
      title: tooltip
    }
  });
  const badgeSlotProps = useSlotProps({
    className: utilityClasses.badge,
    elementType: Badge,
    externalSlotProps: componentsProps?.badge,
    ownerState: void 0,
    additionalProps: {
      "data-testid": `${testId}-badgeRoot`,
      anchorOrigin: defaultBadgeAnchorOrigin,
      as: components?.Badge,
      color: "error",
      invisible: !indicator,
      overlap: "rectangular",
      variant: "dot"
    }
  });
  const iconSlotProps = useSlotProps({
    className: utilityClasses.icon,
    elementType: Icon,
    externalSlotProps: componentsProps?.icon,
    ownerState: void 0,
    additionalProps: {
      as: components?.Icon
    }
  });
  const labelSlotProps = useSlotProps({
    className: utilityClasses.label,
    elementType: Label,
    externalSlotProps: componentsProps?.label,
    ownerState: void 0,
    additionalProps: {
      "data-testid": `${testId}-labelRoot`,
      as: components?.Label
    }
  });
  return /* @__PURE__ */ jsx(Tooltip, { ...tooltipSlotProps, children: /* @__PURE__ */ jsx("span", { children: /* @__PURE__ */ jsxs(Root, { ...rootSlotProps, children: [
    /* @__PURE__ */ jsx(Icon, { ...iconSlotProps, children: /* @__PURE__ */ jsx(Badge, { ...badgeSlotProps, children: icon }) }),
    showLabel && /* @__PURE__ */ jsx(Label, { ...labelSlotProps, children: label })
  ] }) }) });
});
export {
  ToolbarButton
};
//# sourceMappingURL=ToolbarButton.js.map
